import { appLogger } from '@/utils/app-logger/appLogger';
import { isAppDebug } from '@/utils/appDebug';
import {
    decodeMinifiedReactError,
    getMinifiedReactErrorCode,
} from '@/utils/devtools/decode-minified-react-error';
import { isLocalEnv } from '@/utils/getCurrentEnv';

export const DEBUG_ERROR_CAPTURED_EVENT = 'tn:debug-error-captured' as const;

export type DebugErrorSource = 'uncaught' | 'unhandledrejection' | 'error-boundary' | 'hydration';

export type DebugErrorDetail = {
    message: string;
    stack?: string;
    componentStack?: string;
    source: DebugErrorSource;
    capturedAt?: number;
};

type DebugErrorListener = (errors: DebugErrorDetail[]) => void;

type DebugErrorState = {
    errors: DebugErrorDetail[];
    listeners: Set<DebugErrorListener>;
    windowListenerCount: number;
    handleWindowError: ((event: ErrorEvent) => void) | null;
    handleUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null;
    reportWindowError:
        | ((error: Error, logMessage: string, source: DebugErrorSource) => void)
        | null;
    consoleErrorPatched: boolean;
    originalConsoleError: typeof console.error | null;
    originalConsoleWarn: typeof console.warn | null;
    patchedConsoleError: ((...args: unknown[]) => void) | null;
    patchedConsoleWarn: ((...args: unknown[]) => void) | null;
    hydrationSessionExpiresAt: number;
    consoleErrorGuardInstalled: boolean;
    consoleWarnGuardInstalled: boolean;
};

const DEBUG_ERROR_STATE_KEY = Symbol.for('@tn/foundation/debug-error/state');
const DEBUG_ERROR_SCHEDULED_KEY = Symbol.for('@tn/foundation/debug-error/scheduled');
const DUPLICATE_CAPTURE_WINDOW_MS = 2_000;
const MAX_DEBUG_ERRORS = 100;
const HYDRATION_SESSION_WINDOW_MS = 3_000;
const HYDRATION_CONSOLE_PATTERNS = [
    /\bhydration failed\b/i,
    /\berror occurred during hydration\b/i,
    /\bwhile hydrating\b/i,
    /\bserver HTML was replaced with client content\b/i,
    /\bText content did not match\. Server:/i,
    /\bText content does not match server-rendered HTML\b/i,
    /\bdid not match\. Server:/i,
    /\bdoes not match server-rendered HTML\b/i,
    /\bdoes not match what was rendered on the server\b/i,
    /\bExpected server HTML to contain\b/i,
    /\bDid not expect server HTML to contain\b/i,
];
const HYDRATION_REACT_ERROR_CODES = new Set([
    '405',
    '418',
    '419',
    '421',
    '422',
    '423',
    '424',
    '425',
    '436',
]);

function isHydrationMinifiedReactError(text: string): boolean {
    const errorCode = getMinifiedReactErrorCode(text);

    return errorCode !== null && HYDRATION_REACT_ERROR_CODES.has(errorCode);
}
const HYDRATION_FOLLOW_UP_PATTERNS = [
    /component stack/i,
    /check the render method of/i,
    // Match standalone React stack frames, not generic errors whose message includes stack lines.
    /^\s*at /,
];

function getDebugErrorFingerprint(detail: DebugErrorDetail): string {
    return [detail.source, detail.message, detail.stack ?? '', detail.componentStack ?? ''].join(
        '\u0000',
    );
}

function isDuplicateCapture(
    errors: readonly DebugErrorDetail[],
    detail: DebugErrorDetail,
): boolean {
    const fingerprint = getDebugErrorFingerprint(detail);
    const capturedAt = detail.capturedAt ?? Date.now();

    return errors.some((existingError) => {
        if (getDebugErrorFingerprint(existingError) !== fingerprint) {
            return false;
        }

        const existingCapturedAt = existingError.capturedAt ?? 0;

        return capturedAt - existingCapturedAt <= DUPLICATE_CAPTURE_WINDOW_MS;
    });
}

function appendDebugError(errors: DebugErrorDetail[], detail: DebugErrorDetail): void {
    errors.push(detail);

    if (errors.length > MAX_DEBUG_ERRORS) {
        errors.splice(0, errors.length - MAX_DEBUG_ERRORS);
    }
}

function getState(): DebugErrorState {
    const cache = globalThis as Record<symbol, unknown>;
    let state = cache[DEBUG_ERROR_STATE_KEY] as DebugErrorState | undefined;

    if (!state) {
        state = {
            errors: [],
            listeners: new Set(),
            windowListenerCount: 0,
            handleWindowError: null,
            handleUnhandledRejection: null,
            reportWindowError: null,
            consoleErrorPatched: false,
            originalConsoleError: null,
            originalConsoleWarn: null,
            patchedConsoleError: null,
            patchedConsoleWarn: null,
            hydrationSessionExpiresAt: 0,
            consoleErrorGuardInstalled: false,
            consoleWarnGuardInstalled: false,
        };
        cache[DEBUG_ERROR_STATE_KEY] = state;
    }

    return state;
}

function notifyListeners(): void {
    const state = getState();
    const snapshot = [...state.errors];
    state.listeners.forEach((listener) => listener(snapshot));
}

export function getDebugErrors(): readonly DebugErrorDetail[] {
    return getState().errors;
}

export function getDebugError(): DebugErrorDetail | null {
    return getState().errors.at(-1) ?? null;
}

function enrichDebugErrorDetail(detail: DebugErrorDetail): DebugErrorDetail {
    const message = decodeMinifiedReactError(detail.message);
    const stack = detail.stack ? decodeMinifiedReactError(detail.stack) : detail.stack;

    if (message === detail.message && stack === detail.stack) {
        return detail;
    }

    return {
        ...detail,
        message,
        stack,
    };
}

export function formatDebugError(detail: DebugErrorDetail): string {
    const decodedDetail = enrichDebugErrorDetail(detail);
    const sections: string[] = [];

    if (decodedDetail.stack) {
        sections.push(decodedDetail.stack);
    } else if (decodedDetail.message) {
        sections.push(decodedDetail.message);
    }

    if (decodedDetail.componentStack) {
        sections.push(`Component stack:${decodedDetail.componentStack}`);
    }

    return sections.join('\n\n');
}

export function formatDebugErrors(errors: readonly DebugErrorDetail[]): string {
    return errors
        .map((error, index) => {
            const stackText = formatDebugError(error);

            if (stackText) {
                return `--- Error ${index + 1} ---\n${stackText}`;
            }

            return `--- Error ${index + 1} ---\n(no stack trace)`;
        })
        .join('\n\n');
}

export function shouldAutoOpenDevDrawerOnDebugError(): boolean {
    return !isLocalEnv();
}

export function captureDebugError(detail: DebugErrorDetail): void {
    const state = getState();
    const enrichedDetail: DebugErrorDetail = {
        ...enrichDebugErrorDetail(detail),
        capturedAt: detail.capturedAt ?? Date.now(),
    };

    if (isDuplicateCapture(state.errors, enrichedDetail) && enrichedDetail.source !== 'hydration') {
        return;
    }

    appendDebugError(state.errors, enrichedDetail);
    notifyListeners();

    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(
        new CustomEvent<DebugErrorDetail>(DEBUG_ERROR_CAPTURED_EVENT, { detail: enrichedDetail }),
    );

    if (!shouldAutoOpenDevDrawerOnDebugError()) {
        return;
    }

    window.__openDevDrawerOnError = true;
    window.__openDevDrawer?.();
}

export function clearDebugError(): void {
    const state = getState();
    state.errors = [];
    state.hydrationSessionExpiresAt = 0;
    notifyListeners();

    if (typeof window !== 'undefined') {
        window.__openDevDrawerOnError = false;
    }
}

function formatConsoleArgs(args: unknown[]): string {
    const errorArg = args.find((arg): arg is Error => arg instanceof Error);

    if (errorArg) {
        return errorArg.message || 'Hydration error';
    }

    if (args.length === 0) {
        return '';
    }

    const [first, ...rest] = args;

    if (typeof first !== 'string') {
        return args
            .map((arg) => String(arg))
            .join(' ')
            .trim();
    }

    let formatted = first;
    let restIndex = 0;

    formatted = formatted.replace(/%s/g, () => {
        if (restIndex >= rest.length) {
            return '%s';
        }

        return String(rest[restIndex++]);
    });

    if (restIndex < rest.length) {
        formatted = [formatted, ...rest.slice(restIndex).map(String)].join(' ').trim();
    }

    return formatted.trim();
}

function isAppLoggerConsoleOutput(args: unknown[]): boolean {
    return args.some((arg) => typeof arg === 'string' && arg.includes('[appLogger]'));
}

function isHydrationConsoleError(args: unknown[]): boolean {
    return args.some((arg) => {
        if (typeof arg === 'string') {
            return (
                HYDRATION_CONSOLE_PATTERNS.some((pattern) => pattern.test(arg)) ||
                isHydrationMinifiedReactError(arg)
            );
        }

        if (arg instanceof Error) {
            return (
                HYDRATION_CONSOLE_PATTERNS.some((pattern) => pattern.test(arg.message)) ||
                isHydrationMinifiedReactError(arg.message)
            );
        }

        return false;
    });
}

function isHydrationFollowUpConsoleError(args: unknown[], sessionExpiresAt: number): boolean {
    if (sessionExpiresAt === 0 || Date.now() > sessionExpiresAt) {
        return false;
    }

    const formatted = formatConsoleArgs(args);

    return HYDRATION_FOLLOW_UP_PATTERNS.some((pattern) => pattern.test(formatted));
}

function shouldCaptureHydrationConsoleError(args: unknown[], state: DebugErrorState): boolean {
    if (isAppLoggerConsoleOutput(args)) {
        return false;
    }

    const now = Date.now();

    if (isHydrationConsoleError(args)) {
        state.hydrationSessionExpiresAt = now + HYDRATION_SESSION_WINDOW_MS;
        return true;
    }

    return isHydrationFollowUpConsoleError(args, state.hydrationSessionExpiresAt);
}

function parseHydrationConsoleError(args: unknown[]): { message: string; stack?: string } {
    const errorArg = args.find((arg): arg is Error => arg instanceof Error);

    if (errorArg) {
        return {
            message: errorArg.message || 'Hydration error',
            stack: errorArg.stack,
        };
    }

    return {
        message: formatConsoleArgs(args) || 'Hydration error',
    };
}

let isCapturingHydrationConsoleError = false;

function reportHydrationConsoleError(args: unknown[]): void {
    if (isCapturingHydrationConsoleError || isAppLoggerConsoleOutput(args)) {
        return;
    }

    const { message, stack } = parseHydrationConsoleError(args);

    if (!message) {
        return;
    }

    const error = new Error(message);

    if (stack) {
        error.stack = stack;
    }

    isCapturingHydrationConsoleError = true;

    try {
        captureDebugError({
            message,
            stack: error.stack,
            source: 'hydration',
        });
        appLogger.error('Hydration error:', undefined, error);
    } finally {
        isCapturingHydrationConsoleError = false;
    }
}

function createHydrationConsoleHandler(
    state: DebugErrorState,
    originalMethod: (...args: unknown[]) => void,
): (...args: unknown[]) => void {
    return (...args: unknown[]) => {
        const shouldCapture = shouldCaptureHydrationConsoleError(args, state);

        originalMethod(...args);

        if (shouldCapture) {
            reportHydrationConsoleError(args);
        }
    };
}

function installConsoleMethodGuard(state: DebugErrorState, method: 'error' | 'warn'): void {
    const guardInstalledKey =
        method === 'error' ? 'consoleErrorGuardInstalled' : 'consoleWarnGuardInstalled';
    const patchedKey = method === 'error' ? 'patchedConsoleError' : 'patchedConsoleWarn';
    const originalKey = method === 'error' ? 'originalConsoleError' : 'originalConsoleWarn';

    if (state[guardInstalledKey]) {
        return;
    }

    state[guardInstalledKey] = true;

    const wrapImplementation = (implementation: (...args: unknown[]) => void) => {
        state[originalKey] = implementation;
        state[patchedKey] = createHydrationConsoleHandler(state, implementation);

        return state[patchedKey]!;
    };

    let exposedImplementation = wrapImplementation(console[method].bind(console));

    Object.defineProperty(console, method, {
        configurable: true,
        enumerable: true,
        get() {
            return exposedImplementation;
        },
        set(nextImplementation: (...args: unknown[]) => void) {
            if (typeof nextImplementation !== 'function') {
                exposedImplementation = nextImplementation;
                return;
            }

            exposedImplementation = wrapImplementation(nextImplementation.bind(console));
        },
    });
}

/** Registers singleton console.error/warn hooks for React hydration warnings. */
export function registerDebugHydrationErrorCapture(): void {
    if (typeof window === 'undefined' || !isAppDebug()) {
        return;
    }

    const state = getState();

    installConsoleMethodGuard(state, 'error');
    installConsoleMethodGuard(state, 'warn');
    state.consoleErrorPatched = true;
}

function scheduleDebugHydrationErrorCapture(): void {
    registerDebugHydrationErrorCapture();

    if (typeof window === 'undefined') {
        return;
    }

    const cache = globalThis as Record<symbol, boolean | undefined>;

    if (cache[DEBUG_ERROR_SCHEDULED_KEY]) {
        return;
    }

    cache[DEBUG_ERROR_SCHEDULED_KEY] = true;

    window.addEventListener('load', registerDebugHydrationErrorCapture);
    window.setTimeout(registerDebugHydrationErrorCapture, 0);
    window.setTimeout(registerDebugHydrationErrorCapture, 500);
    window.setTimeout(registerDebugHydrationErrorCapture, 2_000);
}

export function subscribeDebugError(callback: DebugErrorListener): () => void {
    const state = getState();
    state.listeners.add(callback);
    callback([...state.errors]);

    return () => {
        state.listeners.delete(callback);
    };
}

export function subscribeDebugErrorCaptured(
    callback: (detail: DebugErrorDetail) => void,
): () => void {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const handler = (event: Event) => {
        const detail = (event as CustomEvent<DebugErrorDetail>).detail;
        if (detail) {
            callback(detail);
        }
    };

    window.addEventListener(DEBUG_ERROR_CAPTURED_EVENT, handler);

    return () => window.removeEventListener(DEBUG_ERROR_CAPTURED_EVENT, handler);
}

export function registerDebugErrorWindowCapture(
    reportError: (error: Error, logMessage: string, source: DebugErrorSource) => void,
): () => void {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const state = getState();
    state.windowListenerCount += 1;
    state.reportWindowError = reportError;

    if (state.windowListenerCount === 1) {
        state.handleWindowError = (event: ErrorEvent) => {
            const error =
                event.error instanceof Error
                    ? event.error
                    : new Error(
                          typeof event.message === 'string' ? event.message : 'Unknown error',
                      );

            state.reportWindowError?.(error, 'Uncaught error:', 'uncaught');
        };

        state.handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const error =
                reason instanceof Error ? reason : new Error(String(reason ?? 'Unknown rejection'));

            state.reportWindowError?.(error, 'Unhandled promise rejection:', 'unhandledrejection');
        };

        window.addEventListener('error', state.handleWindowError);
        window.addEventListener('unhandledrejection', state.handleUnhandledRejection);
    }

    return () => {
        state.windowListenerCount = Math.max(0, state.windowListenerCount - 1);

        if (state.windowListenerCount === 0) {
            if (state.handleWindowError) {
                window.removeEventListener('error', state.handleWindowError);
            }

            if (state.handleUnhandledRejection) {
                window.removeEventListener('unhandledrejection', state.handleUnhandledRejection);
            }

            state.handleWindowError = null;
            state.handleUnhandledRejection = null;
            state.reportWindowError = null;
        }
    };
}

function restoreConsoleMethodForTests(
    method: 'error' | 'warn',
    originalMethod: ((...args: unknown[]) => void) | null,
): void {
    if (!originalMethod) {
        return;
    }

    Object.defineProperty(console, method, {
        configurable: true,
        writable: true,
        enumerable: true,
        value: originalMethod,
    });
}

/** Resets module state between tests. */
export function resetDebugErrorStateForTests(): void {
    const state = getState();

    if (typeof window !== 'undefined') {
        if (state.handleWindowError) {
            window.removeEventListener('error', state.handleWindowError);
        }

        if (state.handleUnhandledRejection) {
            window.removeEventListener('unhandledrejection', state.handleUnhandledRejection);
        }
    }

    state.errors = [];
    state.listeners.clear();
    state.windowListenerCount = 0;
    state.handleWindowError = null;
    state.handleUnhandledRejection = null;
    state.reportWindowError = null;

    if (typeof window !== 'undefined' && state.consoleErrorPatched) {
        restoreConsoleMethodForTests('error', state.originalConsoleError);
        restoreConsoleMethodForTests('warn', state.originalConsoleWarn);
    }

    state.consoleErrorPatched = false;
    state.originalConsoleError = null;
    state.originalConsoleWarn = null;
    state.patchedConsoleError = null;
    state.patchedConsoleWarn = null;
    state.hydrationSessionExpiresAt = 0;
    state.consoleErrorGuardInstalled = false;
    state.consoleWarnGuardInstalled = false;

    if (typeof window !== 'undefined') {
        window.__openDevDrawerOnError = false;
        delete (globalThis as Record<symbol, boolean | undefined>)[DEBUG_ERROR_SCHEDULED_KEY];
    }
}

declare global {
    interface Window {
        __openDevDrawerOnError?: boolean;
        __clearDebugErrors?: () => void;
    }
}

if (typeof window !== 'undefined') {
    window.__clearDebugErrors = clearDebugError;
    scheduleDebugHydrationErrorCapture();
}
